import React from 'react';
import { Link } from 'react-router-dom';
import { FigIcon } from '../components/FigIcon';
import { ArrowLeft, BookOpen, FlaskConical, Scale, Activity, Moon, Wine, Utensils, Brain, Users } from 'lucide-react';

const MethodologyPage = () => {
  const riskFactors = [
    {
      icon: <Scale className="w-6 h-6" />,
      name: "Waist Circumference",
      weight: "25%",
      description: "Waist circumference is the single strongest anthropometric predictor of visceral adipose tissue (VAT) and metabolic syndrome. Unlike BMI, it directly correlates with dangerous intra-abdominal fat accumulation.",
      thresholds: "Men with waist >94cm have elevated risk; >102cm indicates substantially increased cardiometabolic risk.",
      sources: [
        { authors: "Després JP, Lemieux I", title: "Abdominal obesity and metabolic syndrome", journal: "Nature", year: 2006, volume: "444(7121)", pages: "881-887", doi: "10.1038/nature05488" },
        { authors: "Ross R, Neeland IJ, Yamashita S, et al.", title: "Waist circumference as a vital sign in clinical practice: a Consensus Statement", journal: "Nature Reviews Endocrinology", year: 2020, volume: "16(3)", pages: "177-189", doi: "10.1038/s41574-019-0310-7" }
      ]
    },
    {
      icon: <Moon className="w-6 h-6" />,
      name: "Sleep Quality & Duration",
      weight: "15%",
      description: "Sleep deprivation and poor sleep quality impair glucose metabolism, increase cortisol, reduce insulin sensitivity, and dysregulate appetite hormones (leptin/ghrelin). Even one week of sleep restriction can induce pre-diabetic glucose levels.",
      thresholds: "Optimal: 7-9 hours with consistent timing. Risk increases with <6 hours or >9 hours, irregular schedules, or poor subjective quality.",
      sources: [
        { authors: "Spiegel K, Leproult R, Van Cauter E", title: "Impact of sleep debt on metabolic and endocrine function", journal: "The Lancet", year: 1999, volume: "354(9188)", pages: "1435-1439", doi: "10.1016/S0140-6736(99)01376-8" },
        { authors: "Cappuccio FP, D'Elia L, Strazzullo P, Miller MA", title: "Quantity and quality of sleep and incidence of type 2 diabetes", journal: "Diabetes Care", year: 2010, volume: "33(2)", pages: "414-420", doi: "10.2337/dc09-1124" }
      ]
    },
    {
      icon: <Wine className="w-6 h-6" />,
      name: "Alcohol Consumption",
      weight: "12%",
      description: "Alcohol is metabolized preferentially by the liver, disrupting hepatic glucose regulation and promoting fatty liver disease. Regular consumption impairs overnight glucose clearance and contributes to visceral fat accumulation.",
      thresholds: "Low risk: ≤2 drinks/week. Moderate risk: 3-7 drinks/week. High risk: >7 drinks/week or binge drinking patterns.",
      sources: [
        { authors: "Traversy G, Chaput JP", title: "Alcohol Consumption and Obesity: An Update", journal: "Current Obesity Reports", year: 2015, volume: "4(1)", pages: "122-130", doi: "10.1007/s13679-014-0129-4" },
        { authors: "Wannamethee SG, Shaper AG", title: "Alcohol, body weight, and weight gain in middle-aged men", journal: "American Journal of Clinical Nutrition", year: 2003, volume: "77(5)", pages: "1312-1317", doi: "10.1093/ajcn/77.5.1312" }
      ]
    },
    {
      icon: <Activity className="w-6 h-6" />,
      name: "Physical Activity",
      weight: "18%",
      description: "Skeletal muscle is the primary site for glucose disposal. Regular physical activity increases insulin sensitivity, promotes mitochondrial function, and reduces visceral fat independent of weight loss. Both aerobic and resistance training provide benefits.",
      thresholds: "High risk: <150 min/week moderate activity. Optimal: ≥150 min moderate or ≥75 min vigorous activity plus 2+ strength sessions.",
      sources: [
        { authors: "Hawley JA, Lessard SJ", title: "Exercise training-induced improvements in insulin action", journal: "Acta Physiologica", year: 2008, volume: "192(1)", pages: "127-135", doi: "10.1111/j.1748-1716.2007.01783.x" },
        { authors: "Colberg SR, Sigal RJ, Yardley JE, et al.", title: "Physical Activity/Exercise and Diabetes: A Position Statement of the ADA", journal: "Diabetes Care", year: 2016, volume: "39(11)", pages: "2065-2079", doi: "10.2337/dc16-1728" }
      ]
    },
    {
      icon: <Utensils className="w-6 h-6" />,
      name: "Nutrition Timing & Quality",
      weight: "15%",
      description: "Meal timing affects circadian regulation of metabolism. Late eating when insulin sensitivity is lowest promotes hyperglycemia and fat storage. Ultra-processed foods drive inflammation, gut dysbiosis, and metabolic dysfunction independent of calories.",
      thresholds: "Risk factors: eating within 3 hours of bed, skipping breakfast, high ultra-processed food intake (>30% of calories).",
      sources: [
        { authors: "Panda S", title: "Circadian physiology of metabolism", journal: "Science", year: 2016, volume: "354(6315)", pages: "1008-1015", doi: "10.1126/science.aah4967" },
        { authors: "Hall KD, Ayuketah A, Brychta R, et al.", title: "Ultra-Processed Diets Cause Excess Calorie Intake and Weight Gain", journal: "Cell Metabolism", year: 2019, volume: "30(1)", pages: "67-77", doi: "10.1016/j.cmet.2019.05.008" }
      ]
    },
    {
      icon: <Brain className="w-6 h-6" />,
      name: "Chronic Stress",
      weight: "10%",
      description: "Chronic psychological stress elevates cortisol, which promotes visceral fat deposition, increases hepatic glucose output, and impairs insulin signaling. Stress also drives compensatory behaviors (poor sleep, overeating, alcohol use).",
      thresholds: "Assessed via perceived stress scale, work hours, and recovery practices. High chronic stress significantly amplifies other risk factors.",
      sources: [
        { authors: "Björntorp P", title: "Do stress reactions cause abdominal obesity and comorbidities?", journal: "Obesity Reviews", year: 2001, volume: "2(2)", pages: "73-86", doi: "10.1046/j.1467-789x.2001.00027.x" },
        { authors: "Epel ES, McEwen B, Seeman T, et al.", title: "Stress and body shape: stress-induced cortisol secretion is consistently greater among women with central fat", journal: "Psychosomatic Medicine", year: 2000, volume: "62(5)", pages: "623-632", doi: "10.1097/00006842-200009000-00005" }
      ]
    },
    {
      icon: <Users className="w-6 h-6" />,
      name: "Family History",
      weight: "5%",
      description: "Genetic predisposition to type 2 diabetes and metabolic syndrome increases baseline risk. First-degree relatives of diabetics have 2-6x higher lifetime risk. While genes can't be changed, they inform the urgency of lifestyle intervention.",
      thresholds: "Elevated risk: parent or sibling with T2DM, metabolic syndrome, or cardiovascular disease before age 55 (men) or 65 (women).",
      sources: [
        { authors: "Meigs JB, Cupples LA, Wilson PW", title: "Parental transmission of type 2 diabetes: the Framingham Offspring Study", journal: "Diabetes", year: 2000, volume: "49(12)", pages: "2201-2207", doi: "10.2337/diabetes.49.12.2201" },
        { authors: "InterAct Consortium", title: "The link between family history and risk of type 2 diabetes is not explained by anthropometric, lifestyle or genetic risk factors", journal: "Diabetologia", year: 2013, volume: "56(1)", pages: "60-69", doi: "10.1007/s00125-012-2715-x" }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#E8E4DC]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <FigIcon className="w-6 h-6 text-[#2F3E30]" />
            <span className="font-bold text-lg" style={{ fontFamily: 'Libre Baskerville, serif' }}>FIG HEALTH</span>
          </Link>
          <Link to="/" className="text-[#525252] hover:text-[#1A1C19] text-sm flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#D1D1CD] bg-white mb-6">
            <FlaskConical className="w-4 h-4 text-[#2F3E30]" />
            <span className="text-sm text-[#525252]">Evidence-based scoring</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-4 text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>
            OUR METHODOLOGY
          </h1>
          <p className="text-lg text-[#525252] max-w-2xl mx-auto">
            The Fig Health Metabolic Trajectory Score is built on peer-reviewed research linking modifiable lifestyle factors to visceral adiposity and insulin resistance.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-8 mb-12">
            <div className="flex items-start gap-4">
              <BookOpen className="w-8 h-8 text-[#2F3E30] flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold mb-4 text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                  SCORING APPROACH
                </h2>
                <div className="space-y-4 text-[#525252]">
                  <p>
                    Our scoring algorithm synthesizes data from your questionnaire responses across seven validated risk domains. Each domain is weighted based on its relative contribution to metabolic dysfunction as established in epidemiological studies.
                  </p>
                  <p>
                    The final score (0-100) represents your current metabolic trajectory, with higher scores indicating greater risk. The score is not a diagnosis—it's a tool for understanding where you stand and what factors are driving your risk.
                  </p>
                  <p>
                    <strong className="text-[#1A1C19]">Risk Tiers:</strong> Low (0-30), Moderate (31-55), High (56-100). These thresholds are calibrated against population data for metabolic syndrome prevalence.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Risk Factors */}
          <h2 className="text-3xl font-bold mb-8 text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>
            RISK FACTORS & EVIDENCE
          </h2>
          
          <div className="space-y-8">
            {riskFactors.map((factor, index) => (
              <div key={index} className="bg-white border border-[#D1D1CD] p-6 lg:p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-[#2F3E30]/10 flex items-center justify-center text-[#2F3E30] flex-shrink-0">
                    {factor.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-bold text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                        {factor.name.toUpperCase()}
                      </h3>
                      <span className="px-3 py-1 bg-[#2F3E30] text-white text-sm font-medium">
                        Weight: {factor.weight}
                      </span>
                    </div>
                    <p className="text-[#525252] mb-4">{factor.description}</p>
                    <div className="p-4 bg-[#E8E4DC] border border-[#D1D1CD] mb-4">
                      <p className="text-sm">
                        <strong className="text-[#1A1C19]">Thresholds:</strong>{' '}
                        <span className="text-[#525252]">{factor.thresholds}</span>
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Citations */}
                <div className="border-t border-[#D1D1CD] pt-4">
                  <p className="text-xs text-[#525252] uppercase tracking-wide mb-3">Key References</p>
                  <div className="space-y-2">
                    {factor.sources.map((source, sIndex) => (
                      <div key={sIndex} className="text-sm text-[#525252]">
                        <p>
                          {source.authors} ({source.year}). {source.title}. <em>{source.journal}</em>, {source.volume}, {source.pages}.
                          {source.doi && (
                            <a 
                              href={`https://doi.org/${source.doi}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#2F3E30] hover:underline ml-1"
                            >
                              doi:{source.doi}
                            </a>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Limitations */}
      <section className="py-12 px-6 bg-[#EBEBE6]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>
            LIMITATIONS & DISCLAIMERS
          </h2>
          <div className="space-y-6 text-[#525252]">
            <div className="bg-white border border-[#D1D1CD] p-6">
              <h3 className="font-bold text-[#1A1C19] mb-2">Self-Reported Data</h3>
              <p>All data is self-reported and subject to recall bias and measurement error. Waist circumference in particular can vary based on measurement technique. For best accuracy, follow our measurement guidelines carefully.</p>
            </div>
            <div className="bg-white border border-[#D1D1CD] p-6">
              <h3 className="font-bold text-[#1A1C19] mb-2">Not a Clinical Tool</h3>
              <p>The Fig Health score is an educational and motivational tool, not a validated clinical instrument. It has not been prospectively validated against hard endpoints (diabetes diagnosis, cardiovascular events). It should not replace medical evaluation.</p>
            </div>
            <div className="bg-white border border-[#D1D1CD] p-6">
              <h3 className="font-bold text-[#1A1C19] mb-2">Population Averages</h3>
              <p>Risk factor weights are derived from population-level studies and may not reflect individual-level causation. Your personal risk may be higher or lower than the score suggests based on factors not captured (genetics, medications, existing conditions).</p>
            </div>
            <div className="bg-white border border-[#D1D1CD] p-6">
              <h3 className="font-bold text-[#1A1C19] mb-2">Target Population</h3>
              <p>This scoring system is designed for men aged 30-65 without diagnosed diabetes or significant metabolic disease. If you have existing health conditions, consult your physician before making lifestyle changes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Additional References */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>
            ADDITIONAL REFERENCES
          </h2>
          <div className="bg-white border border-[#D1D1CD] p-6 space-y-4 text-sm text-[#525252]">
            <p>
              Alberti KG, Eckel RH, Grundy SM, et al. (2009). Harmonizing the metabolic syndrome: a joint interim statement. <em>Circulation</em>, 120(16), 1640-1645.
              <a href="https://doi.org/10.1161/CIRCULATIONAHA.109.192644" target="_blank" rel="noopener noreferrer" className="text-[#2F3E30] hover:underline ml-1">
                doi:10.1161/CIRCULATIONAHA.109.192644
              </a>
            </p>
            <p>
              Grundy SM, Cleeman JI, Daniels SR, et al. (2005). Diagnosis and management of the metabolic syndrome: an AHA/NHLBI scientific statement. <em>Circulation</em>, 112(17), 2735-2752.
              <a href="https://doi.org/10.1161/CIRCULATIONAHA.105.169404" target="_blank" rel="noopener noreferrer" className="text-[#2F3E30] hover:underline ml-1">
                doi:10.1161/CIRCULATIONAHA.105.169404
              </a>
            </p>
            <p>
              Reaven GM (1988). Role of insulin resistance in human disease. <em>Diabetes</em>, 37(12), 1595-1607.
              <a href="https://doi.org/10.2337/diab.37.12.1595" target="_blank" rel="noopener noreferrer" className="text-[#2F3E30] hover:underline ml-1">
                doi:10.2337/diab.37.12.1595
              </a>
            </p>
            <p>
              Eckel RH, Grundy SM, Zimmet PZ (2005). The metabolic syndrome. <em>The Lancet</em>, 365(9468), 1415-1428.
              <a href="https://doi.org/10.1016/S0140-6736(05)66378-7" target="_blank" rel="noopener noreferrer" className="text-[#2F3E30] hover:underline ml-1">
                doi:10.1016/S0140-6736(05)66378-7
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#D1D1CD]">
        <div className="max-w-4xl mx-auto text-center text-sm text-[#525252]">
          <p className="mb-4">
            Last updated: February 2025. This methodology is periodically reviewed and updated as new evidence emerges.
          </p>
          <p>
            © {new Date().getFullYear()} Fig Health. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MethodologyPage;
